// The Lineup's rules (sprint 29 item 1, roadmap 58): derivation, scoring, votes, bots and the replay the server plays.
// Spec: studio/sprints/29/LINEUP-SPEC.md. Ported number for number from studio/playtest/lineup-sim.js (the oracle).
// Pure and browser-safe, like game/seed.js. Every draw is SHA-256("<seed>:<label>"), first 52 bits as a unit in [0, 1):
// the shift seed plus the lineup's id and the player's recorded actions decide everything, nothing else does.
import { QUESTIONS, cueScore } from './lineup-cues.js';
export { QUESTIONS, cueScore };

// The answers never ride in this file (a browser loads it to check /verify). game/lineup-bank.js registers the base bank on the server;
// a replay with lines of its own (a source, below) needs no bank at all.
let LINEUP_BANK = null;
export const registerLineupBank = bank => { LINEUP_BANK = bank; };
const bankOf = () => { if (!LINEUP_BANK) throw new Error('no lines for this lineup'); return LINEUP_BANK; };
const BANK_AI = 8, BANK_HUMAN = 4; // the base bank's pool sizes: the draws of a lineup dealt from it, and of an old record checked without it

export const LINEUP = {
  size: 6, sizes: [6], offers: 3,
  points: { right: 100, wrong: -100, earlyStep: 10 }, // bonus = earlyStep * (rounds + 1 - k), k = answer rounds seen at the lock
  vote: { noise: 0.3, gain: 1 },                      // an AI votes at random 30% of the time, else by weight 1 + gain * cues seen on that seat
};
export const roundsOf = size => size - 2;
export const bonusOf = (k, size = LINEUP.size) => LINEUP.points.earlyStep * (roundsOf(size) + 1 - k);
export const rightPoints = (k, size) => LINEUP.points.right + bonusOf(k, size);

// ---- SHA-256: the platform's when there is one (Node), else this small pure one (a browser checking /verify) ----
const K = Uint32Array.from('428a2f98 71374491 b5c0fbcf e9b5dba5 3956c25b 59f111f1 923f82a4 ab1c5ed5 d807aa98 12835b01 243185be 550c7dc3 72be5d74 80deb1fe 9bdc06a7 c19bf174 e49b69c1 efbe4786 0fc19dc6 240ca1cc 2de92c6f 4a7484aa 5cb0a9dc 76f988da 983e5152 a831c66d b00327c8 bf597fc7 c6e00bf3 d5a79147 06ca6351 14292967 27b70a85 2e1b2138 4d2c6dfc 53380d13 650a7354 766a0abb 81c2c92e 92722c85 a2bfe8a1 a81a664b c24b8b70 c76c51a3 d192e819 d6990624 f40e3585 106aa070 19a4c116 1e376c08 2748774c 34b0bcb5 391c0cb3 4ed8aa4a 5b9cca4f 682e6ff3 748f82ee 78a5636f 84c87814 8cc70208 90befffa a4506ceb bef9a3f7 c67178f2'.split(' ').map(h => parseInt(h, 16)));
export function sha256Pure(text) {
  const data = new TextEncoder().encode(text), bits = data.length * 8;
  const padded = new Uint8Array((data.length + 9 + 63) & ~63);
  padded.set(data); padded[data.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bits / 2 ** 32)); view.setUint32(padded.length - 4, bits >>> 0);
  const H = Uint32Array.of(0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19), w = new Uint32Array(64);
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3), s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i++) {
      const t1 = (h + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) | 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    [a, b, c, d, e, f, g, h].forEach((x, i) => { H[i] = (H[i] + x) | 0; });
  }
  return [...H].map(x => (x >>> 0).toString(16).padStart(8, '0')).join('');
}
const nodeCrypto = globalThis.process?.getBuiltinModule?.('node:crypto');
export const sha256Sync = text => (nodeCrypto ? nodeCrypto.createHash('sha256').update(text).digest('hex') : sha256Pure(text));
export const unit = (seed, label) => parseInt(sha256Sync(`${seed}:${label}`).slice(0, 13), 16) / 2 ** 52;

export const labels = {
  human: id => `lineup:${id}:human`,
  deck: (id, i) => `lineup:${id}:deck:${i}`,
  person: (id, r) => `lineup:${id}:h:${r}`,
  ai: (id, r, i) => `lineup:${id}:p:${r}:${i}`,
  voteNoise: (id, r, v) => `lineup:${id}:vn:${r}:${v}`,
  votePick: (id, r, v) => `lineup:${id}:vp:${r}:${v}`,
  tie: (id, r) => `lineup:${id}:tie:${r}`,
};

// Fisher-Yates driven by labelled units: step i (n-1 down to 1) swaps position i with floor(u * (i + 1)). rec(label, j) logs each draw.
function shuffled(n, seed, labelOf, rec) {
  const a = [...Array(n).keys()];
  for (let i = n - 1; i >= 1; i--) {
    const label = labelOf(n - 1 - i), j = Math.floor(unit(seed, label) * (i + 1));
    rec?.(label, j);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
// Weighted pick: first candidate whose running share of the total weight exceeds u.
function pickWeighted(cands, weights, u) {
  const total = weights.reduce((s, w) => s + w, 0); let acc = 0;
  for (let i = 0; i < cands.length; i++) { acc += weights[i] / total; if (u < acc) return cands[i]; }
  return cands[cands.length - 1];
}

export function derive(seed, id, size = LINEUP.size, rec) {
  const label = labels.human(id), human = Math.floor(unit(seed, label) * size);
  rec?.(label, human);
  const deck = shuffled(QUESTIONS.length, seed, i => labels.deck(id, i), rec);
  const aiSeats = [...Array(size).keys()].filter(s => s !== human); // rank in this list = the seat's index in the per-round AI draw
  return { seed, id, size, human, deck, aiSeats };
}
export const offersOf = (L, r) => L.deck.slice(LINEUP.offers * (r - 1), LINEUP.offers * r); // r is 1-based
// The cards of round r for the question q (bank index), for the seats still in.
export function answersOf(L, r, q, seatsIn, rec) {
  const bank = bankOf()[q], { order, hi } = lineDraws(L, r, bank.ai.length, bank.human.length, rec);
  const cards = {};
  for (const s of seatsIn) cards[s] = s === L.human ? bank.human[hi] : bank.ai[order[L.aiSeats.indexOf(s)]];
  return cards;
}
function lineDraws(L, r, aiN, humanN, rec) {
  const order = shuffled(aiN, L.seed, i => labels.ai(L.id, r, i), rec);
  const label = labels.person(L.id, r), hi = Math.floor(unit(L.seed, label) * humanN);
  rec?.(label, hi);
  return { order, hi };
}
// The five AIs' votes (the person seat never votes). cum[seat] = cue points of that seat's cards seen so far, this round included.
export function votesOf(L, r, seatsIn, cum, rec) {
  const votes = {};
  for (const v of seatsIn) {
    if (v === L.human) continue;
    const cands = seatsIn.filter(s => s !== v);
    const nl = labels.voteNoise(L.id, r, v), pl = labels.votePick(L.id, r, v);
    const noisy = unit(L.seed, nl) < LINEUP.vote.noise;
    const w = noisy ? cands.map(() => 1) : cands.map(s => 1 + LINEUP.vote.gain * cum[s]);
    votes[v] = pickWeighted(cands, w, unit(L.seed, pl));
    rec?.(nl, noisy); rec?.(pl, votes[v]);
  }
  return votes;
}
export function outOf(L, r, votes, seatsIn, rec) {
  const n = {}; for (const t of Object.values(votes)) n[t] = (n[t] ?? 0) + 1;
  const top = Math.max(...seatsIn.map(s => n[s] ?? 0));
  const tied = seatsIn.filter(s => (n[s] ?? 0) === top);
  const label = labels.tie(L.id, r), out = tied[Math.floor(unit(L.seed, label) * tied.length)];
  rec?.(label, out);
  return out;
}
export const tallyOf = votes => { const n = {}; for (const t of Object.values(votes)) n[t] = (n[t] ?? 0) + 1; return n; };

// ---- The replay: a lineup is its seed, its id and the player's actions. Phases: ask -> voted -> (ask | final) -> done. ----
// actions: { a: 'ask', i } | { a: 'continue' } | { a: 'send', seat } | { a: 'lock', seat } | { a: 'walk', why? }.
// An action the rules do not allow throws. rolls: every draw in order, { label, result }.
// The lines are a third input since sprint 31. By default (no source) they are dealt from the registered bank by seeded draws, as before.
// A source { cardsFor(r, q, seatsIn, L) -> { seat: line }, legacyRolls? } carries the lines the room really dealt (a model's, or the pool's)
// and the replay takes them as they are. legacyRolls makes it log the old line draws too, so a record made before sprint 31 still matches
// roll for roll while its lines come from the record and not from a bank.
export function replay(seed, id, actions = [], size = LINEUP.size, source = null) {
  const rolls = [], rec = (label, result) => rolls.push({ label, result });
  const L = derive(seed, id, size, rec), R = roundsOf(size);
  const st = { L, size, R, phase: 'ask', k: 0, seatsIn: [...Array(size).keys()], gone: [], rounds: [], cum: Object.fromEntries([...Array(size).keys()].map(s => [s, 0])), outcome: null, rolls };
  const done = (o) => { st.phase = 'done'; st.outcome = { human: L.human, locked: null, ...o }; };
  const remove = (seat, by) => {
    st.seatsIn = st.seatsIn.filter(s => s !== seat); st.gone.push({ seat, r: st.k, by });
    st.phase = st.k === R ? 'final' : 'ask';
  };
  const seatOk = s => Number.isInteger(s) && st.seatsIn.includes(s);
  for (const act of actions) {
    if (st.phase === 'done') throw new Error('lineup is over');
    if (act?.a === 'ask') {
      if (st.phase !== 'ask' || !Number.isInteger(act.i) || act.i < 0 || act.i >= LINEUP.offers) throw new Error('cannot ask now');
      const r = st.k + 1, q = offersOf(L, r)[act.i];
      let cards, cues;
      if (source?.cuesFor) { // the public record: the cue score of each line, never the line (sprint 31 fix pass)
        cards = {}; cues = source.cuesFor(r);
        if (!cues || st.seatsIn.some(s => !Number.isFinite(cues[s]))) throw new Error('no cues for this round');
        if (source.legacyRolls) lineDraws(L, r, BANK_AI, BANK_HUMAN, rec);
      } else if (source) {
        cards = source.cardsFor(r, q, st.seatsIn, L);
        if (!cards || st.seatsIn.some(s => typeof cards[s] !== 'string')) throw new Error('no lines for this round');
        if (source.legacyRolls) lineDraws(L, r, BANK_AI, BANK_HUMAN, rec);
      } else cards = answersOf(L, r, q, st.seatsIn, rec);
      for (const s of st.seatsIn) st.cum[s] += cues ? cues[s] : cueScore(cards[s]);
      const votes = votesOf(L, r, st.seatsIn, st.cum, rec);
      st.rounds.push({ r, q, i: act.i, offers: offersOf(L, r), cards, votes, cum: { ...st.cum }, seatsIn: [...st.seatsIn] });
      st.k = r; st.phase = 'voted';
    } else if (act?.a === 'continue') {
      if (st.phase !== 'voted') throw new Error('nothing to carry out');
      const round = st.rounds.at(-1), out = outOf(L, st.k, round.votes, st.seatsIn, rec);
      round.removed = out; round.how = 'vote';
      if (out === L.human) done({ points: 0, end: 'caught', k: st.k });
      else remove(out, 'vote');
    } else if (act?.a === 'send') {
      if (st.phase !== 'voted' || !seatOk(act.seat)) throw new Error('cannot send that seat away');
      const round = st.rounds.at(-1); round.removed = act.seat; round.how = 'send';
      if (act.seat === L.human) done({ points: LINEUP.points.wrong, end: 'wrong', k: st.k, sentHuman: true });
      else remove(act.seat, 'send');
    } else if (act?.a === 'lock') {
      if (!['ask', 'voted', 'final'].includes(st.phase) || !seatOk(act.seat)) throw new Error('cannot lock that seat');
      const right = act.seat === L.human;
      done({ points: right ? rightPoints(st.k, size) : LINEUP.points.wrong, end: right ? 'right' : 'wrong', k: st.k, locked: act.seat });
    } else if (act?.a === 'walk') done({ points: 0, end: 'walk', k: st.k, ...(act.why === 'shift' ? { why: 'shift' } : {}) });
    else throw new Error('unknown action');
  }
  return st;
}

// ---- Bots (the reference's), for the playtest: everything they read is on the table; none of them sees L.human ----
// Play a lineup with a strategy: strat({ L, r, seatsIn, cards, cum, votes, gone, history }) -> null | { lock: seat } | { send: seat }.
export function lineupPlay(seed, id, strat, size = LINEUP.size) {
  const L = derive(seed, id, size), R = roundsOf(size);
  let seatsIn = [...Array(size).keys()]; const cum = Object.fromEntries(seatsIn.map(s => [s, 0])), gone = [], history = [];
  const first = strat({ L, r: 0, seatsIn, cards: {}, cum, votes: {}, gone, history });
  if (first?.lock != null) return finish(L, first.lock, 0);
  for (let r = 1; r <= R; r++) {
    const q = offersOf(L, r)[0]; // the bots always ask the first offer (every question has equal pools)
    const cards = answersOf(L, r, q, seatsIn);
    for (const s of seatsIn) cum[s] += cueScore(cards[s]);
    const votes = votesOf(L, r, seatsIn, cum);
    history.push({ cards, votes });
    const act = strat({ L, r, seatsIn, cards, cum: { ...cum }, votes, gone, history });
    if (act?.lock != null) return finish(L, act.lock, r);
    let leaves = outOf(L, r, votes, seatsIn);
    if (act?.send != null) { leaves = act.send; if (leaves === L.human) return { points: LINEUP.points.wrong, end: 'wrong', k: r, locked: null, human: L.human, sentHuman: true }; }
    else if (leaves === L.human) return { points: 0, end: 'caught', k: r, locked: null, human: L.human };
    gone.push(leaves); seatsIn = seatsIn.filter(s => s !== leaves);
  }
  return { points: 0, end: 'walk', k: R, locked: null, human: L.human };
}
function finish(L, seat, k) {
  const right = seat === L.human;
  return { points: right ? rightPoints(k, L.size) : LINEUP.points.wrong, end: right ? 'right' : 'wrong', k, locked: seat, human: L.human };
}
export function evidenceBot({ margin = 3, voteWeight = 1, protect = false } = {}) {
  const seen = {};
  return ({ r, seatsIn, cum, votes, history, L }) => {
    if (r === 0) return null;
    for (const t of Object.keys(seen)) delete seen[t];
    for (const h of history) for (const [, t] of Object.entries(h.votes)) seen[t] = (seen[t] ?? 0) + 1;
    const score = s => cum[s] + voteWeight * (seen[s] ?? 0);
    const ranked = [...seatsIn].sort((a, b) => score(b) - score(a) || a - b);
    const gap = score(ranked[0]) - score(ranked[1]);
    if (gap >= margin || r === roundsOf(L.size)) return { lock: ranked[0] };
    // Protect: if the vote is about to remove the leading suspect, send the least suspicious seat away instead (the vote does not happen).
    if (protect && outOf(L, r, votes, seatsIn) === ranked[0]) return { send: ranked[ranked.length - 1] };
    return null;
  };
}
// Random locker: picks the round it locks in (0..R) and a random seat still in, from its own labelled draws.
export function randomBot(seed, id) {
  return ({ r, seatsIn, L }) => {
    const when = Math.floor(unit(seed, `bot:${id}:when`) * (roundsOf(L.size) + 1));
    if (r !== when) return null;
    return { lock: seatsIn[Math.floor(unit(seed, `bot:${id}:who`) * seatsIn.length)] };
  };
}

export function study({ runs = 200, lineups = 60, margin = 3, size = LINEUP.size, protect = false } = {}) {
  let beat = 0, evSum = 0, rdSum = 0, evRight = 0, evLocks = 0, caught = 0, walks = 0, rdRight = 0, rdLocks = 0, kSum = 0, chain = 0, chains = 0;
  for (let run = 0; run < runs; run++) {
    const seed = sha256Sync('lineup-run' + run);
    let ev = 0, rd = 0, streak = 0;
    for (let i = 0; i < lineups; i++) {
      const id = `${run}-${i}`;
      const a = lineupPlay(seed, id, evidenceBot({ margin, protect }), size), b = lineupPlay(seed, id, randomBot(seed, id), size);
      ev += a.points; rd += b.points;
      if (a.locked != null) { evLocks++; kSum += a.k; if (a.end === 'right') evRight++; }
      if (a.end === 'caught') caught++; if (a.end === 'walk') walks++;
      if (b.locked != null) { rdLocks++; if (b.end === 'right') rdRight++; }
      if (a.end === 'right') streak++; else if (a.end === 'wrong') { chain += streak; chains++; streak = 0; }
    }
    chain += streak; chains++;
    evSum += ev; rdSum += rd; if (ev > rd) beat++;
  }
  const n = runs * lineups;
  return {
    runs, lineups, margin, size,
    evidenceBeatsRandomPct: +(100 * beat / runs).toFixed(1),
    evidenceAvgPerLineup: +(evSum / n).toFixed(1), randomAvgPerLineup: +(rdSum / n).toFixed(1),
    evidenceRightLockPct: +(100 * evRight / Math.max(1, evLocks)).toFixed(1), randomRightLockPct: +(100 * rdRight / Math.max(1, rdLocks)).toFixed(1),
    evidenceAvgLockRound: +(kSum / Math.max(1, evLocks)).toFixed(2), caughtPct: +(100 * caught / n).toFixed(1), walkPct: +(100 * walks / n).toFixed(1),
    evidenceAvgRightLocksBeforeAWrongOne: +(chain / Math.max(1, chains)).toFixed(2),
  };
}
